import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MarketingNav from '../components/layout/MarketingNav';
import { authService } from '../services/api';
import { Button, Card, Input } from '../components/ui';

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const ResetPassword = () => {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Reset token is missing. Please use the link from your email.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword({
        token,
        newPassword,
        confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      console.error('Failed to reset password:', err);
      setError(err.response?.data?.message || 'Unable to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 py-12 mt-20">
        <div className="w-full max-w-md">
          <Card>
            <div className="text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">MHDF Momentum</p>
              <h1 className="mt-2 text-3xl font-display font-semibold text-neutral-900">Reset Password</h1>
              <p className="mt-2 text-neutral-500">Choose a new password to regain access.</p>
            </div>

            {error && (
              <div className="mt-4 rounded-soft border border-error-200 bg-error-50 p-3 text-sm text-error-700">
                {error}
              </div>
            )}

            {success ? (
              <div className="mt-6 rounded-soft border border-success-200 bg-success-50 p-4 text-sm text-success-700">
                Your password has been reset. You can now sign in.
                <div className="mt-3">
                  <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                    Go to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <Input
                  label="New Password"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  endIcon={showNewPassword ? EyeOffIcon : EyeIcon}
                  onEndIconClick={() => setShowNewPassword((prev) => !prev)}
                  fullWidth
                  required
                />
                <Input
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  endIcon={showConfirmPassword ? EyeOffIcon : EyeIcon}
                  onEndIconClick={() => setShowConfirmPassword((prev) => !prev)}
                  fullWidth
                  required
                />

                <Button type="submit" fullWidth disabled={loading} className="w-full">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            <p className="mt-4 text-center text-sm text-neutral-500">
              Return to{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                Sign In
              </Link>
            </p>
          </Card>
        </div>
      </main>
    </>
  );
};

export default ResetPassword;
